/**
 * Releases and updates (docs/plans/2026-10-update-management.md).
 *
 * The customer portal publishes every product's releases. Mojo Up AI Cloud (`service.mojoup.ai`)
 * serves them to devices and to Enterprise AIOE deployments at `/releases/v1`; an Enterprise
 * AIOE follows Cloud's index, approves releases per ring and serves the same routes to its own
 * fleet. The manifest is the workbench's `ReleaseInfo` (`desktop/src/main/updates.ts`) with a
 * few fields added, so the desktop's updater reads `latest.json` unchanged. The workbench
 * mirrors this file.
 *
 *   POST /releases/v1/sync                                      pull upstream now (one a minute); release pipelines call it after publishing
 *   GET  /releases/v1/index.json                                every release this platform knows
 *   GET  /releases/v1/:product/:channel/latest.json             what this caller should run (device token optional)
 *   GET  /releases/v1/:product/:version/assets/:name            302 to the bytes
 *   GET  /releases/v1/install/:name                             an install script (INSTALL_SCRIPTS), text
 *   GET  /admin/v1/releases                                     the console's view (ReleasesOverview)
 *   GET|PUT /admin/v1/releases/policy                           ReleasePolicy
 *   POST /admin/v1/releases/sync                                pull upstream now
 *   POST /admin/v1/releases/:product/:version/approve { ring }  approve mode: offer this version to one ring
 *   POST /admin/v1/releases/:product/:version/mirror            hold the bytes here
 *
 * Pushed updates and rollbacks (the workbench's docs/plans/2026-10-pushed-updates.md, D170): an
 * administrator orders a machine, or every machine in a ring, to check or install now; the order rides
 * on the heartbeat answer (fleet.ts `heartbeatAnswerSchema.update`) until the machine reports the
 * wanted version or the deadline passes. A rollback is a per-machine pin to an older release plus
 * `allowDowngrade`. Mojo Up AI Cloud orders nothing.
 *
 *   POST /admin/v1/fleet/devices/:deviceId/update               UpdateOrderCreate -> UpdateOrderRecord
 *   POST /admin/v1/releases/:product/rings/:ring/update         UpdateOrderCreate (sans product) -> UpdateOrderRecord[]
 *   GET  /admin/v1/fleet/updates                                pending and recent orders
 *   DELETE /admin/v1/fleet/updates/:id                          cancel a pending order
 *   DELETE /admin/v1/releases/:product/pins/:deviceId           clear a machine's pin (it follows its ring again)
 */
import { z } from "zod";
export const RELEASES_SCHEMA_VERSION = 1;
export const RELEASES_PATH = "/releases/v1";
/**
 * The install scripts every platform serves at `/releases/v1/install/<name>` (fetched from upstream and
 * cached), so a one-line install works on a network that reaches only this platform. The scripts are the
 * workbench's (`desktop/scripts/install/node.{sh,ps1}`, `scripts/install-workbench-linux.sh`).
 */
export const INSTALL_SCRIPTS = ["install-node.sh", "install-node.ps1", "install-workbench-linux.sh"];
export const RELEASE_PRODUCTS = ["workbench", "node", "mesh", "aioe"];
export const releaseProductSchema = z.enum(RELEASE_PRODUCTS);
/** The desktop's two channel names; the node's `preview` is read as `nightly`. */
export const releaseChannelSchema = z.enum(["stable", "nightly"]);
export const releasePlatformSchema = z.enum(["win32", "darwin", "linux", "any"]);
export const releaseArchSchema = z.enum(["x64", "arm64", "any"]);
export const releaseArtifactKindSchema = z.enum(["nsis", "appimage", "deb", "dmg", "zip", "tar.gz", "image", "chart"]);
/** A semantic version, with an optional pre-release (`0.47.0-nightly.20261006.3`). */
export const releaseVersionSchema = z.string().regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/, "a semantic version");
export const releaseArtifactSchema = z.object({
    platform: releasePlatformSchema,
    arch: releaseArchSchema,
    kind: releaseArtifactKindSchema,
    name: z.string().min(1).max(300),
    /** Where the bytes are; the platform's `assets/:name` route redirects here unless it mirrors. */
    url: z.string().url(),
    /** Hex SHA-256 of the bytes; the updater refuses anything else. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size: z.number().int().nonnegative().optional(),
    signature: z.enum(["authenticode", "codesign", "cosign"]).optional(),
    /**
     * What an artifact is for (D447). `node`: the node's container image; `agent-sandbox`: the image agents run in on a cluster.
     * For `kind: "image"`, `url` is the registry manifest URL (`https://ghcr.io/v2/<name>/manifests/sha256:...`), `platform` is
     * `linux` and `sha256` the digest's hex. An image is never a download: the platform lists it and never fetches its bytes.
     */
    purpose: z.enum(["node", "agent-sandbox"]).optional(),
    /** The image's digest, `sha256:<64 hex>`. */
    digest: z.string().regex(/^sha256:[0-9a-f]{64}$/).optional(),
});
export const releaseManifestSchema = z.object({
    product: releaseProductSchema,
    version: releaseVersionSchema,
    channel: releaseChannelSchema,
    publishedAt: z.string(),
    /**
     * `security` and `reliability` (a fix for something that stops machines working, such as a request
     * storm) are the safety floor: no ring's delay holds them back, and a machine cut off from its
     * organisation may take one from Mojo Up's public feed.
     */
    severity: z.enum(["normal", "security", "reliability"]).optional(),
    /** Markdown, short; `page` is the full notes on the docs site. */
    releaseNotes: z.string().max(20_000).optional(),
    page: z.string().url().optional(),
    /** The oldest version that updates straight to this one; older ones are offered the newest release they can take. */
    minimumFrom: releaseVersionSchema.optional(),
    /** Kept for older Windows apps, which read these two before `artifacts`. */
    downloadUrl: z.string().url().optional(),
    installer: z.object({ name: z.string().min(1).max(300), sha256: z.string().regex(/^[0-9a-f]{64}$/) }).optional(),
    artifacts: z.array(releaseArtifactSchema).max(50),
    /** Withdrawn: still listed, never offered. */
    yanked: z.object({ at: z.string(), reason: z.string().max(500) }).optional(),
    /** The build's policy manifest (policyManifest.ts): its digest and where the document is served. A platform fetches it once per digest. */
    policyManifest: z.object({ digest: z.string().regex(/^[0-9a-f]{64}$/), url: z.string().url() }).optional(),
});
/** `GET /releases/v1/index.json`: newest first, every product and channel. */
export const releaseIndexSchema = z.object({
    schemaVersion: z.literal(RELEASES_SCHEMA_VERSION),
    generatedAt: z.string(),
    /** The platform that generated it (its public URL); an enterprise checks the signature came from its upstream. */
    issuer: z.string().url().optional(),
    releases: z.array(releaseManifestSchema).max(5000),
});
/** The JWS `typ` Cloud signs its index with (controlSignature.ts `signJws`; the key at `/.well-known/aioe-jwks.json`). */
export const RELEASE_INDEX_TYP = "aioe-release-index+jws";
/** The header that carries the index's signature: a JWS whose claims are `{ iss, iat, sha256 }` of the body. */
export const RELEASE_INDEX_SIGNATURE_HEADER = "x-aioe-index-signature";
/**
 * On every `latest.json` answer (200 and 204): the organisation's update policy for that product and
 * that machine, so a machine that later falls back to Mojo Up's public feed keeps to it.
 * `mode=follow|approve; approved=<v>; pinned=<v>; defer-days=<n>; unattended=allowed|forbidden`;
 * `approved` and `pinned` only when set.
 */
export const RELEASE_POLICY_HEADER = "x-aioe-release-policy";
export function formatReleasePolicyHeader(p) {
    return [`mode=${p.mode}`, ...(p.approved ? [`approved=${p.approved}`] : []), ...(p.pinned ? [`pinned=${p.pinned}`] : []), `defer-days=${p.deferDays}`, `unattended=${p.unattended}`].join("; ");
}
// ── Enterprise control ────────────────────────────────────────────────────────
const ringIdSchema = z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9-]*$/);
/** Which devices a ring holds. A device is in the first ring whose match it fits; a ring without `match` is everyone else. */
export const releaseRingSchema = z.object({
    id: ringIdSchema,
    name: z.string().min(1).max(100),
    /**
     * The channel this ring's machines follow for every product, overriding each product's channel in
     * follow mode. An Enterprise machine takes what its ring's channel offers whatever channel it asked
     * for (the workbench treats the manifest's `channel` from its organisation as the decision).
     */
    channel: releaseChannelSchema.optional(),
    match: z
        .object({
        tags: z.array(z.string().min(1).max(64)).max(32).optional(),
        deviceIds: z.array(z.string().min(1).max(100)).max(500).optional(),
        kind: z.enum(["desktop", "node"]).optional(),
    })
        .optional(),
});
export const releaseProductPolicySchema = z.object({
    channel: releaseChannelSchema.default("stable"),
    /** follow: every upstream release is offered as it lands, after `deferDays`. approve: only what `approved` names. */
    mode: z.enum(["follow", "approve"]).default("follow"),
    deferDays: z.number().int().min(0).max(90).default(0),
    /** approve mode: the version each ring is offered. */
    approved: z.record(ringIdSchema, releaseVersionSchema).default({}),
    /** Hold the bytes in this platform's blob store and serve them from here: every new release on the channel is held as it lands. */
    mirror: z.boolean().default(false),
    /**
     * How many of the newest held releases on the channel to keep. Older held bytes are dropped by the
     * hourly sweep, except for a version a machine runs, a ring is approved to, or a pin names: those
     * are kept while that is so. Downloads of a dropped version fall back to the upstream address.
     */
    keepMirrored: z.number().int().min(1).max(50).default(3),
    /** Per-machine overrides: the version one device is offered whatever its ring says (a rollback pins an older one). */
    pins: z.record(z.string().min(1).max(100), releaseVersionSchema).default({}),
    /**
     * Whether a machine may install a security or reliability release on its own when it cannot reach
     * this platform for a day (it then reads Mojo Up's public feed). `forbidden`: it waits for this one.
     */
    unattended: z.enum(["allowed", "forbidden"]).default("allowed"),
});
export const DEFAULT_RELEASE_RING = { id: "everyone", name: "Everyone" };
export const releasePolicySchema = z.object({
    rings: z.array(releaseRingSchema).min(1).max(20).default([DEFAULT_RELEASE_RING]),
    products: z.partialRecord(releaseProductSchema, releaseProductPolicySchema).default({}),
});
export const releaseApproveSchema = z.object({ ring: ringIdSchema });
/** One product's state as the console shows it. */
export const releaseProductViewSchema = z.object({
    product: releaseProductSchema,
    policy: releaseProductPolicySchema,
    /** Versions the fleet runs, with how many devices run each (desktops and nodes; empty for mesh until machines report it). */
    running: z.array(z.object({ version: z.string(), devices: z.number().int().nonnegative() })),
    /** What the platform offers now, per ring (absent when nothing is approved or known). */
    offered: z.record(ringIdSchema, releaseVersionSchema),
    /** How many machines of this product's kind sit in each ring. */
    ringSizes: z.record(ringIdSchema, z.number().int().nonnegative()),
    /** The newest non-yanked release on the policy's channel upstream. */
    latest: releaseVersionSchema.optional(),
    /** Mirrored versions held in this platform's blob store. */
    mirrored: z.array(releaseVersionSchema),
    /** The bytes those take in the blob store, when it can say. */
    mirroredBytes: z.number().int().nonnegative().optional(),
    releases: z.array(releaseManifestSchema),
});
export const releasesOverviewSchema = z.object({
    edition: z.enum(["enterprise", "cloud"]),
    /** Where this platform follows releases from; absent when it has none configured. */
    upstream: z.string().url().optional(),
    lastSyncAt: z.string().optional(),
    lastSyncError: z.string().optional(),
    rings: z.array(releaseRingSchema),
    /** How many devices fall in each ring now. */
    ringSizes: z.record(ringIdSchema, z.number().int().nonnegative()),
    /** Which ring each device falls in, so the fleet list can say why a machine is on nightly builds. */
    ringOf: z.record(z.string(), ringIdSchema),
    products: z.array(releaseProductViewSchema),
    /** This platform's own version, and the newest `aioe` release known. */
    platform: z.object({ version: z.string(), latest: releaseManifestSchema.optional() }),
});
// ── Pushed updates and rollbacks ──────────────────────────────────────────────
export const updateOrderProductSchema = z.enum(["workbench", "node", "mesh"]);
export const updateOrderActionSchema = z.enum(["check", "install"]);
/** What rides on the heartbeat answer: the machine acts on an `id` once, and again after ten minutes. */
export const updateOrderSchema = z.object({
    id: z.string().min(1).max(100),
    action: updateOrderActionSchema,
    product: updateOrderProductSchema,
    /** What to expect; the feed is what is installed. */
    version: releaseVersionSchema.optional(),
    /** A rollback: an older version is taken. */
    allowDowngrade: z.boolean().optional(),
    /** Shown to the person. */
    reason: z.string().max(500).optional(),
    requestedBy: z.string().max(200).optional(),
    requestedAt: z.string().optional(),
});
/** `POST /admin/v1/fleet/devices/:deviceId/update` and, without `product` fixed by the path, a ring's. */
export const updateOrderCreateSchema = z.object({
    product: updateOrderProductSchema,
    action: updateOrderActionSchema,
    /** The release to install: pins the machine (or every machine in the ring) to it first. Required for a rollback. */
    version: releaseVersionSchema.optional(),
    allowDowngrade: z.boolean().default(false),
    reason: z.string().max(500).optional(),
});
export const ringUpdateOrderCreateSchema = updateOrderCreateSchema.omit({ product: true });
export const updateOrderStateSchema = z.enum(["pending", "done", "expired", "cancelled"]);
/** One order as the console sees it. */
export const updateOrderRecordSchema = z.object({
    id: z.string(),
    deviceId: z.string(),
    product: updateOrderProductSchema,
    action: updateOrderActionSchema,
    version: z.string().optional(),
    allowDowngrade: z.boolean(),
    reason: z.string().optional(),
    requestedBy: z.string(),
    requestedAt: z.string(),
    deadlineAt: z.string(),
    state: updateOrderStateSchema,
    /** How many heartbeats carried it. */
    deliveries: z.number().int().nonnegative(),
    /** The version the machine last reported while the order was open. */
    reportedVersion: z.string().optional(),
    doneAt: z.string().optional(),
    /** A ring order's id, shared by every machine's order it created. */
    batchId: z.string().optional(),
    ring: z.string().optional(),
});
/** Hours an install order is repeated before it is dropped as not carried out. */
export const UPDATE_ORDER_INSTALL_DEADLINE_HOURS = 24;
/** Minutes a check order is repeated; it is done once two heartbeats have carried it. */
export const UPDATE_ORDER_CHECK_DEADLINE_MINUTES = 30;
// ── Helpers shared with the workbench ─────────────────────────────────────────
/** Full semantic-version order with pre-releases below their release (semver 2.0 §11). Negative when a < b. */
export function compareVersions(a, b) {
    const [ca, pa] = split(a);
    const [cb, pb] = split(b);
    for (let i = 0; i < 3; i++) {
        const d = (ca[i] ?? 0) - (cb[i] ?? 0);
        if (d)
            return d;
    }
    if (!pa.length && !pb.length)
        return 0;
    if (!pa.length)
        return 1;
    if (!pb.length)
        return -1;
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const x = pa[i];
        const y = pb[i];
        if (x === undefined)
            return -1;
        if (y === undefined)
            return 1;
        const nx = /^\d+$/.test(x);
        const ny = /^\d+$/.test(y);
        if (nx && ny) {
            const d = Number(x) - Number(y);
            if (d)
                return d;
        }
        else if (nx !== ny)
            return nx ? -1 : 1;
        else if (x !== y)
            return x < y ? -1 : 1;
    }
    return 0;
}
function split(v) {
    // Only the first hyphen starts the pre-release: `1.0.0-rc-1` is core 1.0.0, pre-release rc-1.
    const dash = v.indexOf("-");
    const core = dash < 0 ? v : v.slice(0, dash);
    const pre = dash < 0 ? "" : v.slice(dash + 1);
    return [core.split(".").map((n) => Number(n) || 0), pre ? pre.split(".") : []];
}
/** The newest release of a product on a channel that is not yanked and (when given) not newer than `cap`. */
export function newestRelease(releases, product, channel, cap) {
    let best;
    for (const r of releases) {
        if (r.product !== product || r.channel !== channel || r.yanked)
            continue;
        if (cap && compareVersions(r.version, cap) > 0)
            continue;
        if (!best || compareVersions(r.version, best.version) > 0)
            best = r;
    }
    return best;
}
//# sourceMappingURL=releases.js.map